import { send } from '../../../src/network/network';
import { type GetPromoResponse } from '../../../src/types/GetPromoResponse';

/**
 * The below code is temporary until we implement a UTM tracking system backed directly into signup.
 * Once we have that, we can remove this code and use the UTM tracking system directly.
 *
 * As a hack, we can use the Django middleware on a GET request right before signup to capture the UTM params and store them in Django session storage.
 */
const UTM_SESSION_STORAGE_KEY = 'hr_utm_data';
const UTM_PARAMS = {
  campaign: 'utm_campaign',
  medium: 'utm_medium',
  source: 'utm_source',
  term: 'utm_term',
  content: 'utm_content',
} as const;

/**
 * Gets UTM params where those params originally came from the Query String and were captured in session storage.
 *
 * This can be used for any Astro pages where the layout is using `default.astro`
 * or where `<Utm />` is in included component.
 */
function getUtmParamsFromSessionStorage() {
  let utmParams: {
    campaign: null | string;
    source: null | string;
    medium: null | string;
    term: null | string;
    content: null | string;
  } = {
    campaign: null,
    source: null,
    medium: null,
    term: null,
    content: null,
  };

  const utmSessionStorage = sessionStorage.getItem(UTM_SESSION_STORAGE_KEY);
  if (utmSessionStorage) {
    try {
      const parsed = JSON.parse(utmSessionStorage);

      if (parsed) {
        utmParams = {
          campaign: parsed.campaign || null,
          source: parsed.source || null,
          medium: parsed.medium || null,
          term: parsed.term || null,
          content: parsed.content || null,
        };
      }
    } catch {}
  }

  return utmParams;
}

export { type GetPromoResponse };

type PromoPages = 'homepage' | '/limited-time/' | '/signup/';

export type Params = {
  page: PromoPages;
  promoCode?: string;
};

export async function getPromoData(params: Params) {
  const query = new URLSearchParams();
  const keys = Object.keys(params) as (keyof typeof params)[];
  keys.forEach((paramName) => {
    const value = params[paramName];
    if (value === undefined) {
      return;
    }
    query.append(paramName === 'promoCode' ? 'promo_code' : paramName, value);
  });

  const utmParams = getUtmParamsFromSessionStorage();
  Object.entries(utmParams).forEach(([key, value]) => {
    if (value) {
      query.append(UTM_PARAMS[key as keyof typeof UTM_PARAMS], value);
    }
  });

  // Critical: If promo exists in session storage, add it to the query params so it's applied on checkout
  // The homepage partially handles this if it's in the QS, but if the user lands on the quiz with it in query string and
  // then persisted to session storage, the promo wasn't getting applied on signup/checkout.
  try {
    const promoCode = sessionStorage.getItem('promo');

    if (promoCode) {
      query.append('promo', promoCode);
    }
  } catch {}

  return send<GetPromoResponse>({
    path: `/api/get_promo_data/`,
    method: 'GET',
    query,
  });
}
